import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FilterGroup, FilterGroupConfig } from '../models/e621.models';

@Injectable({
  providedIn: 'root',
})
export class FilterGroupService {
  private readonly STORAGE_KEY = 'drawfiend-custom-filter-groups';

  private readonly defaultGroups: FilterGroup[] = this.buildDefaultGroups();
  private customGroups: FilterGroup[] = [];
  private readonly groups$ = new BehaviorSubject<FilterGroup[]>([]);

  constructor() {
    this.customGroups = this.loadCustomGroups();
    this.emitAll();
  }

  /** Observable of all filter groups (custom first, then defaults), sorted alphabetically within each section. */
  getFilterGroups(): Observable<FilterGroup[]> {
    return this.groups$.asObservable();
  }

  /** Synchronous snapshot of all groups. */
  getAllGroups(): FilterGroup[] {
    return this.groups$.getValue();
  }

  /** Find a group by its id. */
  getGroupById(id: string): FilterGroup | undefined {
    return this.getAllGroups().find(g => g.id === id);
  }

  /** Add a custom filter group from the current search options state. */
  addCustomGroup(name: string, config: FilterGroupConfig): FilterGroup {
    const group: FilterGroup = {
      id: this.generateId(),
      name,
      config: { ...config },
      isDefault: false,
    };
    this.customGroups.push(group);
    this.persist();
    this.emitAll();
    return group;
  }

  /** Remove a custom filter group. Default groups cannot be removed. */
  removeCustomGroup(id: string): boolean {
    const idx = this.customGroups.findIndex(g => g.id === id);
    if (idx === -1) return false;
    this.customGroups.splice(idx, 1);
    this.persist();
    this.emitAll();
    return true;
  }

  /** Rename a custom filter group. */
  renameCustomGroup(id: string, newName: string): boolean {
    const group = this.customGroups.find(g => g.id === id);
    if (!group) return false;
    group.name = newName;
    this.persist();
    this.emitAll();
    return true;
  }

  /** Get only default groups. */
  getDefaultGroups(): FilterGroup[] {
    return [...this.defaultGroups];
  }

  /** Get the Drawfiend Mode filter group (the featured default). */
  getDrawfiendModeGroup(): FilterGroup | undefined {
    return this.defaultGroups.find(g => g.id === 'default_drawfiend_mode');
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  private emitAll(): void {
    const custom = [...this.customGroups].sort((a, b) => a.name.localeCompare(b.name));
    const featured = this.defaultGroups.filter(g => g.isFeatured);
    const regular = this.defaultGroups.filter(g => !g.isFeatured).sort((a, b) => a.name.localeCompare(b.name));
    this.groups$.next([...custom, ...featured, ...regular]);
  }

  private persist(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.customGroups));
    } catch (e) {
      console.error('Failed to save custom filter groups:', e);
    }
  }

  private loadCustomGroups(): FilterGroup[] {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as FilterGroup[];
        return parsed.map(g => ({ ...g, isDefault: false }));
      }
    } catch (e) {
      console.error('Failed to load custom filter groups:', e);
    }
    return [];
  }

  private generateId(): string {
    return 'fg_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }

  private baseConfig(): FilterGroupConfig {
    const now = new Date();
    return {
      advancedDateMode: 'none',
      periodUnit: 'week',
      periodOffset: 0,
      exactGranularity: 'day',
      exactYear: now.getFullYear(),
      exactMonth: now.getMonth() + 1,
      exactDay: now.getDate(),
      rangeGranularity: 'month',
      rangeFromYear: now.getFullYear() - 1,
      rangeFromMonth: now.getMonth() + 1,
      rangeFromDay: now.getDate(),
      rangeToYear: now.getFullYear(),
      rangeToMonth: now.getMonth() + 1,
      rangeToDay: now.getDate(),
      orderBy: '',
      minScore: null,
      maxScore: null,
    };
  }

  private buildDefaultGroups(): FilterGroup[] {
    const base = this.baseConfig();
    const now = new Date();
    const currentYear = now.getFullYear();

    return [
      {
        id: 'default_drawfiend_mode',
        name: '✨ Drawfiend Mode ✨',
        isDefault: true,
        isFeatured: true,
        config: {
          ...base,
          advancedDateMode: 'period',
          periodUnit: 'day',
          periodOffset: 1,
          orderBy: 'score_asc',
          minScore: 300,
          tags: 'rating:e',
        },
      },
      {
        id: 'default_trending_today',
        name: 'Trending Today',
        isDefault: true,
        config: { ...base, advancedDateMode: 'period', periodUnit: 'today', orderBy: 'score' },
      },
      {
        id: 'default_trending_week',
        name: 'Trending This Week',
        isDefault: true,
        config: { ...base, advancedDateMode: 'period', periodUnit: 'week', orderBy: 'score' },
      },
      {
        id: 'default_trending_month',
        name: 'Trending This Month',
        isDefault: true,
        config: { ...base, advancedDateMode: 'period', periodUnit: 'month', orderBy: 'score' },
      },
      {
        id: 'default_trending_year',
        name: 'Trending This Year',
        isDefault: true,
        config: { ...base, advancedDateMode: 'period', periodUnit: 'year', orderBy: 'score' },
      },
      {
        id: 'default_new_today',
        name: 'New Today',
        isDefault: true,
        config: { ...base, advancedDateMode: 'period', periodUnit: 'today', orderBy: 'created_at' },
      },
      {
        id: 'default_new_week',
        name: 'New This Week',
        isDefault: true,
        config: { ...base, advancedDateMode: 'period', periodUnit: 'week', orderBy: 'created_at' },
      },
      {
        id: 'default_random_all_time',
        name: 'Random (All Time)',
        isDefault: true,
        config: { ...base, orderBy: 'random' },
      },
      {
        id: 'default_random_high_score',
        name: 'Random High Score',
        isDefault: true,
        config: { ...base, orderBy: 'random', minScore: 200 },
      },
      {
        id: 'default_top_all_time',
        name: 'Top Rated (All Time)',
        isDefault: true,
        config: { ...base, orderBy: 'score' },
      },
      {
        id: 'default_most_faved',
        name: 'Most Favorited (All Time)',
        isDefault: true,
        config: { ...base, orderBy: 'favcount' },
      },
      {
        id: 'default_longest_duration',
        name: 'Longest Duration',
        isDefault: true,
        config: { ...base, orderBy: 'duration' },
      },
      {
        id: 'default_lowest_score',
        name: 'Lowest Score First',
        isDefault: true,
        config: { ...base, orderBy: 'score_asc' },
      },
      {
        id: 'default_classics',
        name: 'Classic Gems (Older High Score)',
        isDefault: true,
        config: {
          ...base,
          advancedDateMode: 'range',
          rangeGranularity: 'year',
          rangeFromYear: 2012,
          rangeFromMonth: 1,
          rangeFromDay: 1,
          rangeToYear: currentYear - 2,
          rangeToMonth: 12,
          rangeToDay: 31,
          orderBy: 'score',
        },
      },
      {
        id: 'default_last_year_best',
        name: 'Best of Last Year',
        isDefault: true,
        config: {
          ...base,
          advancedDateMode: 'range',
          rangeGranularity: 'year',
          rangeFromYear: currentYear - 1,
          rangeFromMonth: 1,
          rangeFromDay: 1,
          rangeToYear: currentYear - 1,
          rangeToMonth: 12,
          rangeToDay: 31,
          orderBy: 'score',
        },
      },
      {
        id: 'default_hidden_gems',
        name: 'Hidden Gems (Low Score, Recent)',
        isDefault: true,
        config: {
          ...base,
          advancedDateMode: 'period',
          periodUnit: 'month',
          orderBy: 'random',
          minScore: 10,
          maxScore: 50,
        },
      },
    ];
  }
}
